import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Button, Paper,  Grid, 
  CardMedia, Skeleton, CardContent 
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CategoriaDto, ServicoDto, AgendamentoStep } from '../../agendamento.types';
import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { AgendamentoService } from '../../services/agendamentoservice';

interface StepServicoProps {
  codigoUnidade: number;
}

export const StepServico: React.FC<StepServicoProps> = ({ codigoUnidade }) => {
  const [categorias, setCategorias] = useState<CategoriaDto[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Seleção de estados específicos da store para evitar re-renderizações desnecessárias
  const setServico = useAgendamentoStore((state) => state.setServico);
  const setStep = useAgendamentoStore((state) => state.setStep);

  useEffect(() => {
    // Garante que o código seja tratado como número antes da chamada
    const idUnidade = Number(codigoUnidade);
    
    if (!isNaN(idUnidade)) {
      AgendamentoService.listarServicos(idUnidade)
        .then((data) => setCategorias(data))
        .catch((err) => console.error("Erro ao carregar serviços:", err))
        .finally(() => setLoading(false));
    }
  }, [codigoUnidade]);

  // Função para selecionar e avançar
  const handleSelect = (item: ServicoDto) => {
    setServico(item);
    // Cast explícito para number para evitar o erro ts(2362) em futuras operações
    setStep(AgendamentoStep.HORARIOS as number);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton 
            key={i} 
            variant="rounded" 
            height={110} 
            sx={{ borderRadius: 4, bgcolor: 'rgba(255,255,255,0.05)' }} 
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {categorias.map((cat) => (
        <Box key={cat.codigo}>
          <Typography 
            variant="h6" 
            fontWeight={800} 
            sx={{ mb: 3, display: 'flex', alignItems: 'center', color: 'var(--color-text)' }}
          >
            <Box sx={{ width: 4, height: 24, bgcolor: 'var(--color-primary)', mr: 2, borderRadius: 1 }} />
            {cat.nome}
          </Typography>

          <Grid container spacing={3}>
            {cat.produtos.map((item) => {
              // Tratamento de imagem Base64
              const fotoUrl = item.foto?.startsWith('data:image') 
                ? item.foto 
                : item.foto ? `data:image/png;base64,${item.foto.replace(/\s/g, '')}` : undefined;

              return (
                <Grid key={item.codigo} size={{ xs: 12 }}>
                  <Paper 
                    elevation={0} 
                    onClick={() => handleSelect(item)}
                    sx={{ 
                      borderRadius: 4, 
                      overflow: 'hidden', 
                      cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.08)',
                      bgcolor: 'var(--color-surface)',
                      transition: '0.2s ease-in-out', 
                      '&:hover': { 
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                        borderColor: 'var(--color-primary)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                      {item.foto && (
                        <CardMedia
                          component="img"
                          sx={{ 
                            width: { xs: '100%', sm: 150 }, 
                            height: { xs: 150, sm: 'auto' }, 
                            objectFit: 'cover' 
                          }}
                          image={fotoUrl}
                          alt={item.descricao}
                        />
                      )}
                      
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3 }}>
                        <Typography variant="h6" fontWeight={800} sx={{ color: 'var(--color-text)', mb: 0.5 }}>
                          {item.descricao}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'var(--color-primary)' }} />
                          <Typography variant="caption" fontWeight={600} sx={{ color: 'var(--color-text)' }}>
                            {item.tempoExecucao} min
                          </Typography>
                        </Box>
                      </CardContent>
                      
                      <Box sx={{ 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: { xs: 'row', sm: 'column' }, 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        bgcolor: 'rgba(255,255,255,0.02)',
                        minWidth: 160,
                        gap: 2,
                        borderLeft: { sm: '1px solid rgba(255,255,255,0.05)' }
                      }}>
                        <Typography variant="h6" fontWeight={900} sx={{ color: 'var(--color-primary)' }}>
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valor)}
                        </Typography>
                        
                        <Button 
                          variant="contained" 
                          sx={{ 
                            bgcolor: 'var(--color-primary)', 
                            color: '#000', 
                            borderRadius: 2, 
                            textTransform: 'none', 
                            fontWeight: 900,
                            px: 3,
                            '&:hover': { bgcolor: 'var(--color-primary)', filter: 'brightness(1.1)' }
                          }}
                        >
                          Agendar
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};